import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { ClipLoader } from "react-spinners";
import CustomSelect from "views/Components/CustomSelect";
import { ROLE_MAP } from "services/constants";
import localization from "services/localization";
import ResourceComponent from "views/Components/ResourceLibrary/ResourceComponent";
import { useUserStore } from "context/useUserStore";
import {
  getDepartmentTypes,
  SUBJECTS,
  GRADES_OPTIONS,
} from "services/constants";

const AddEditDepartmentModal = ({
  show,
  title,
  formData,
  errors,
  onChange,
  onSubmit,
  onClose,
  loading,
  users = [],
  onFormChange,
  modules,
}) => {
  const { userData } = useUserStore();
  const [type, setType] = useState(formData?.type || null);
  const [subject, setSubject] = useState(null);
  const [grade, setGrade] = useState(null);
  const [assignedUser, setAssignedUser] = useState(null);
  const [members, setMembers] = useState([]);
  const [usersOptions, setUsersOptions] = useState([]);
  const [subjectOptions, setSubjectOptions] = useState(SUBJECTS);
  const [theme, setTheme] = useState(
    localStorage.getItem("SA_user_theme") || "dark",
  );
  const [moduleOptions, setModuleOptions] = useState([]);

  const DEPARTMENT_TYPE_OPTIONS = getDepartmentTypes();

  useEffect(() => {
    if (show) {
      setTheme(localStorage.getItem("SA_user_theme") || "dark");
    }
  }, [show]);

  useEffect(() => {
    if (show) {
      getUserData();
    }
  }, [show, users]);

  useEffect(() => {
    if (!show || !formData?.subject) return;

    let existingOption = subjectOptions.find(
      (opt) => opt.value === formData.subject,
    );
    if (!existingOption) {
      existingOption = {
        label: formData.subject,
        value: formData.subject,
      };

      setSubjectOptions((prev) => [...prev, existingOption]);
    }
    setSubject(existingOption);
  }, [show, formData?.subject]);

  useEffect(() => {
    if (formData) {
      setType(
        DEPARTMENT_TYPE_OPTIONS.find((t) => t.value === formData.type) || null,
      );
    }
  }, [formData]);

  useEffect(() => {
    if (!modules || !type) return;

    let filteredModules = [];

    if (type.value === "classroom") {
      filteredModules = modules.filter((m) => m.type === "teaching");
    } else if (type.value === "hr" || type.value === "administrative") {
      filteredModules = modules.filter((m) => m.type === "administration");
    } else if (type.value === "utilities") {
      filteredModules = modules;
    }

    const groupedModules = Object.values(
      filteredModules.reduce((acc, m) => {
        if (!acc[m.type]) {
          acc[m.type] = {
            label: m.type.charAt(0).toUpperCase() + m.type.slice(1),
            options: [],
          };
        }

        acc[m.type].options.push({
          value: m.id,
          label: m.name,
        });

        return acc;
      }, {}),
    );

    setModuleOptions(groupedModules);
  }, [modules, type]);

  const getUserData = () => {
    console.log("users", users);
    setUsersOptions(
      users.map((u) => ({
        value: u.id,
        label: `${u.name} (${u.email})`,
        role: u.role,
        school_id: u.school_id,
      })),
    );
  };

  const filterUsersByRoles = (roles = []) => {
    if (!roles.length) return [];
    return usersOptions.filter((u) => roles.includes(u.role));
  };

  const assignedUserOptions = type
    ? (() => {
        let roles = ROLE_MAP[type.value]?.assigned || [];

        if (type.value === "utilities") {
          roles = ["teacher", "administrative"];
        }

        return filterUsersByRoles(roles).filter((u) =>
          u?.school_id?.includes(userData?.selected_school_id),
        );
      })()
    : [];

  const memberOptions = type
    ? filterUsersByRoles(ROLE_MAP[type.value]?.members).filter((u) =>
        u?.school_id?.includes(userData?.selected_school_id),
      )
    : [];

  const handleSelectChange = (field, selectedOption) => {
    if (field === "type") setType(selectedOption);
    if (field === "grade") setGrade(selectedOption);
    if (field === "user_id") setAssignedUser(selectedOption);
    if (field === "subject" && selectedOption) {
      const normalizedOption = {
        value: selectedOption.value ?? selectedOption.label,
        label: selectedOption.label ?? selectedOption.value,
      };

      const exists = subjectOptions.some(
        (opt) => opt.value === normalizedOption.value,
      );

      if (!exists) {
        setSubjectOptions((prev) => [...prev, normalizedOption]);
      }

      setSubject(normalizedOption);
    }

    onChange({
      target: { name: field, value: selectedOption?.value || "" },
    });
  };

  const handleModulesChange = (selected) => {
    onChange({
      target: {
        name: "module_id",
        value: selected ? selected.map((m) => m.value) : [],
      },
    });
  };

  const handleMembersChange = (selected) => {
    setMembers(selected);
    onChange({
      target: { name: "members", value: selected.map((m) => m.value) },
    });
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onClose}
        centered
        size="lg"
        className="themed-modal addEditModal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>

        <Form
          onSubmit={(e) => {
            onSubmit(e);
          }}
        >
          <Modal.Body>
            <Row>
              {/* Department Name */}
              <Col md={6} className="mb-3">
                <Form.Group className="required">
                  <Form.Label className="bootstrap-label">
                    {localization.department_name || "Department Name"}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name || ""}
                    onChange={onChange}
                    placeholder={
                      localization.department_name || "Enter department name"
                    }
                    isInvalid={!!errors.name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.name}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              {/* Type */}
              <Col md={6} className="mb-3">
                <Form.Group className="required">
                  <Form.Label className="bootstrap-label">
                    {localization.type || "Type"}
                  </Form.Label>
                  <CustomSelect
                    options={DEPARTMENT_TYPE_OPTIONS}
                    value={type}
                    onChange={(selected) =>
                      handleSelectChange("type", selected)
                    }
                    placeholder={localization.select_type || "Select type"}
                  />
                  {errors.type && (
                    <div className="text-danger small mt-1">{errors.type}</div>
                  )}
                </Form.Group>
              </Col>

              {/* Conditional fields for classroom */}
              {type?.value === "classroom" && (
                <>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="bootstrap-label">
                        {localization.subject || "Subject"}
                      </Form.Label>
                      <CustomSelect
                        options={subjectOptions}
                        value={subject}
                        onChange={(selected) =>
                          handleSelectChange("subject", selected)
                        }
                        placeholder={
                          localization.select_subject || "Select subject"
                        }
                        isCreatable={true}
                      />
                      {errors.subject && (
                        <div className="text-danger small mt-1">
                          {errors.subject}
                        </div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="bootstrap-label">
                        {localization.grade || "Grade"}{" "}
                      </Form.Label>
                      <CustomSelect
                        options={GRADES_OPTIONS}
                        value={GRADES_OPTIONS.find(
                          (g) => g.value === formData.grade,
                        )}
                        onChange={(selected) =>
                          handleSelectChange("grade", selected)
                        }
                        placeholder={
                          localization.select_grade || "Select grade"
                        }
                      />
                      {errors.grade && (
                        <div className="text-danger small mt-1">
                          {errors.grade}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </>
              )}

              {/* Assigned User */}
              <Col md={6} className="mb-3">
                <Form.Group className="required">
                  <Form.Label className="bootstrap-label">
                    {localization.assigned_user || "Assigned User"}
                  </Form.Label>
                  <CustomSelect
                    options={assignedUserOptions}
                    value={assignedUserOptions.find(
                      (u) => u.value === formData.user_id,
                    )}
                    onChange={(selected) =>
                      handleSelectChange("user_id", selected)
                    }
                    placeholder={localization.select_user || "Select user"}
                  />
                  {errors.user_id && (
                    <div className="text-danger small mt-1">
                      {errors.user_id}
                    </div>
                  )}
                </Form.Group>
              </Col>

              {/* Members */}
              {formData.type != "utilities" && (
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label className="bootstrap-label">
                      {localization.members || "Members"}
                    </Form.Label>
                    <CustomSelect
                      isMulti
                      options={memberOptions}
                      value={memberOptions.filter(
                        (u) =>
                          formData.members &&
                          formData.members.includes(u.value),
                      )}
                      onChange={handleMembersChange}
                      placeholder={
                        localization.select_members || "Select members"
                      }
                    />
                  </Form.Group>
                </Col>
              )}

              {/* Start & End Dates */}
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label className="bootstrap-label">
                    {localization.start_date || "Start Date"}
                  </Form.Label>
                  <Form.Control
                    type="date"
                    name="start_date"
                    value={formData.start_date || ""}
                    onChange={onChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label className="bootstrap-label">
                    {localization.end_date || "End Date"}
                  </Form.Label>
                  <Form.Control
                    type="date"
                    name="end_date"
                    value={formData.end_date || ""}
                    onChange={onChange}
                  />
                </Form.Group>
              </Col>

              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label className="bootstrap-label">
                    {localization.modules || "Modules"}
                  </Form.Label>

                  <CustomSelect
                    isMulti
                    options={moduleOptions}
                    value={moduleOptions
                      .flatMap((group) => group.options)
                      .filter((m) =>
                        (formData.module_id || []).includes(m.value),
                      )}
                    onChange={handleModulesChange}
                    placeholder={
                      localization.select_modules || "Select modules"
                    }
                  />
                </Form.Group>
              </Col>
              {/* Description */}
              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label className="bootstrap-label">
                    {localization.description || "Description"}
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="description"
                    value={formData.description || ""}
                    onChange={onChange}
                    placeholder={
                      localization.brief_description || "Brief description"
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={12} className="mb-3">
                <Form.Group>
                  <ResourceComponent
                    selectedResources={formData.resources || []}
                    onSelectResources={(resources) => {
                      onFormChange("resources", resources);
                    }}
                  />
                </Form.Group>
              </Col>
              <hr />
            </Row>
          </Modal.Body>

          <Modal.Footer>
            <Button className="action-btn secondary" onClick={onClose}>
              {localization.cancel || "Cancel"}{" "}
            </Button>
            <Button
              className="action-btn primary no-border"
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <ClipLoader color="var(--primary-icon)" size={20} />
              ) : (
                localization.save || "Save"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default AddEditDepartmentModal;
